/**
 * TanStack Query Hooks for Bulk Download Operations
 *
 * Provides React hooks for downloading table and linked table data
 * in various formats (JSON, CSV, XLSX, XML).
 *
 * Uses signed tokens to bypass CF Worker limits for large datasets.
 *
 * @module features/tables/hooks/useBulkDownload
 */

import { useMutation } from '@tanstack/react-query';

import { useRpc } from '@/lib/rpc';
import { toast } from '@/lib/toast';
import {
  downloadTableBulk,
  downloadLinkedTableBulk,
  type DownloadFormat,
  type DownloadStatus,
} from '@/lib/bulkDownload';

export type { DownloadFormat, DownloadStatus };

/**
 * Hook to download table data in bulk
 *
 * @example
 * ```tsx
 * const { mutate: downloadTable, isPending } = useTableBulkDownload();
 *
 * const handleDownload = () => {
 *   downloadTable({
 *     tableId: 'uuid',
 *     versionId: 'uuid',
 *     format: 'xlsx',
 *   });
 * };
 * ```
 */
export function useTableBulkDownload() {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async ({
      tableId,
      versionId,
      format,
    }: {
      tableId: string;
      versionId: string;
      format: DownloadFormat;
    }) => {
      return downloadTableBulk(rpc, tableId, versionId, format);
    },
    // No success toast - the downloaded file is the feedback
    onError: (error) => {
      toast.error('Download failed', error instanceof Error ? error.message : undefined);
    },
  });
}

/**
 * Hook to download linked table data in bulk
 *
 * @example
 * ```tsx
 * const { mutate: downloadLinkedTable, isPending } = useLinkedTableBulkDownload();
 *
 * const handleDownload = () => {
 *   downloadLinkedTable({
 *     linkedTableId: 'uuid',
 *     format: 'csv',
 *     tableVersions: {
 *       'orders': 'version-uuid-1',
 *       'products': 'version-uuid-2',
 *     },
 *   });
 * };
 * ```
 */
export function useLinkedTableBulkDownload() {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async ({
      linkedTableId,
      format,
      tableVersions,
    }: {
      linkedTableId: string;
      format: DownloadFormat;
      tableVersions?: Record<string, string>;
    }) => {
      return downloadLinkedTableBulk(rpc, linkedTableId, format, tableVersions);
    },
    // No success toast - the downloaded file is the feedback
    onError: (error) => {
      toast.error('Download failed', error instanceof Error ? error.message : undefined);
    },
  });
}
